// Adapter over the vendored Fabric Table extension (PR #10852, mauricekindermann).
// The real Table (extends Group, custom TableLayoutStrategy, full toObject/fromObject,
// double-click cell editing, resize controls, keyboard nav) lives in ./table/.
// Importing Table here runs its classRegistry.setClass(Table,'Table') so loadFromJSON
// reconstructs tables automatically. This module exposes the small surface the editor
// needs: create, structure ops, programmatic cell text (for the panel + batch).
import type { Canvas } from 'fabric'
import { Table, initTableInteraction, getEditingCell } from './table'
import { getCanvas, state } from './../state'
import { labelPx } from './../canvas'
import { lq } from './../liquid'

export { Table }

// ---- Evaluated-Liquid preview overlay --------------------------------------
// While a STATIC Table is the active object and a dataset is imported, cells
// containing {{ ... }} show their EVALUATED value (root scope — same as the
// document renderer) instead of the raw template; the cell being text-edited
// is skipped so editing always shows the template. Selected-only rendering
// keeps the overlay out of exports (they discardActiveObject first).
// Implemented as a render wrap so the vendored Table.ts stays untouched.
// DynamicTable overrides render and draws its own ghost row; its constructor
// type differs, which is how we skip it here.
const origTableRender = Table.prototype.render
Table.prototype.render = function (this: Table, ctx: CanvasRenderingContext2D) {
  origTableRender.call(this, ctx)
  if ((this.constructor as any).type !== 'Table') return
  if (this.canvas?.getActiveObject?.() !== this) return
  const rows = state.batchData
  if (!rows?.length) return

  const editing = getEditingCell()
  const scope = { rows }
  const m = this.calcTransformMatrix()
  let drewAny = false
  ctx.save()
  ctx.transform(m[0], m[1], m[2], m[3], m[4], m[5])
  for (let r = 0; r < this.rows; r++) {
    for (let c = 0; c < this.cols; c++) {
      if (editing && editing.table === this && editing.row === r && editing.col === c) continue
      const cell: any = this.getCell(r, c)
      const tx: any = this.getCellText(r, c)
      const raw: string = tx?.text ?? ''
      if (!raw.includes('{{') || !cell || cell._isMerged) continue
      const x = cell.left - cell.width / 2
      const y = cell.top - cell.height / 2
      ctx.save()
      ctx.beginPath(); ctx.rect(x + 1, y + 1, cell.width - 2, cell.height - 2); ctx.clip()
      ctx.fillStyle = typeof cell.fill === 'string' && cell.fill.startsWith('#') ? cell.fill : '#ffffff'
      ctx.fillRect(x + 1, y + 1, cell.width - 2, cell.height - 2)
      const fam = tx?.fontFamily && tx.fontFamily !== 'Times New Roman' ? tx.fontFamily : 'Inter, system-ui, sans-serif'
      ctx.font = `italic ${tx?.fontWeight === 'bold' ? '700 ' : ''}${tx?.fontSize ?? 13}px ${fam}`
      ctx.fillStyle = '#1d4ed8'
      ctx.textBaseline = 'middle'
      const pad = this.cellPadding ?? 8
      const align = tx?.textAlign ?? 'center'
      ctx.textAlign = align === 'right' ? 'right' : align === 'center' ? 'center' : 'left'
      const txX = align === 'right' ? x + cell.width - pad : align === 'center' ? x + cell.width / 2 : x + pad
      ctx.fillText(lq(raw, scope), txX, y + cell.height / 2)
      ctx.restore()
      drewAny = true
    }
  }
  if (drewAny) {
    ctx.font = '10px Inter, system-ui, sans-serif'
    ctx.fillStyle = '#64748b'
    ctx.textAlign = 'left'
    ctx.fillText('↻ evaluated preview — double-click a cell to edit its expression', -this.width / 2, this.height / 2 + 11)
  }
  ctx.restore()
}

export interface TableModel { rows: number; cols: number; data: string[][] }

export function isTable(o: unknown): o is Table {
  return o instanceof Table
}

// Enable dblclick-edit / cell-select / resize / keyboard once per canvas.
const wired = new WeakSet<Canvas>()
export function enableTableInteraction(canvas: Canvas = getCanvas()): void {
  if (wired.has(canvas)) return
  wired.add(canvas)
  initTableInteraction(canvas)
}

export function addTable(rows = 3, cols = 3): Table {
  const { w, h } = labelPx()
  const cellWidth = Math.max(40, Math.min(90, (w * 0.85) / cols))
  const t = new Table(rows, cols, { cellWidth, minCellHeight: 22, left: w / 2, top: h / 2 })
  const canvas = getCanvas()
  enableTableInteraction(canvas)
  canvas.add(t)
  canvas.setActiveObject(t)
  canvas.requestRenderAll()
  return t
}

function reflow(t: Table): void {
  // triggerLayoutWithAnchor is the same private reflow addRow/addColumn use.
  ;(t as any).triggerLayoutWithAnchor?.()
  getCanvas().requestRenderAll()
}

export function addRow(t: Table): Table { t.addRow(); getCanvas().requestRenderAll(); return t }
export function removeRow(t: Table): Table { t.removeRow(); getCanvas().requestRenderAll(); return t }
export function addColumn(t: Table): Table { t.addColumn(); getCanvas().requestRenderAll(); return t }
export function removeColumn(t: Table): Table { t.removeColumn(); getCanvas().requestRenderAll(); return t }

export function setCell(t: Table, row: number, col: number, text: string): Table {
  const tx = t.getCellText(row, col)
  if (tx) { tx.set('text', text); reflow(t) }
  return t
}

// Cell styling — applies to all cells via the extension's reactive updaters.
// Pass 'transparent' (or width 0) to hide fills / borders.
export function setCellFill(t: Table, color: string): Table { t.updateCellFill(color); getCanvas().requestRenderAll(); return t }
export function setCellStroke(t: Table, color: string): Table { t.updateCellStroke(color); getCanvas().requestRenderAll(); return t }
export function setBorderWidth(t: Table, width: number): Table { t.updateBorderWidth(width); getCanvas().requestRenderAll(); return t }

export interface TableStyle { fill: string; stroke: string; strokeWidth: number }
export function getTableStyle(t: Table): TableStyle {
  return { fill: (t as any).cellFill, stroke: (t as any).cellStroke, strokeWidth: (t as any).cellStrokeWidth }
}

// ---- Excel-like per-cell styling ----------------------------------------
// All operations target the currently SELECTED cells (click / shift-click a cell
// on canvas), or the WHOLE table if nothing is selected.
export type CellAlign = 'left' | 'center' | 'right'
export type CellVAlign = 'top' | 'middle' | 'bottom'
export type BorderStyle = 'solid' | 'dashed' | 'dotted'

const DASH: Record<BorderStyle, number[] | undefined> = { solid: undefined, dashed: [6, 4], dotted: [2, 3] }

function targetPositions(t: Table): Array<{ row: number; col: number }> {
  const sel = (t as any)._selectedCells as Array<{ row: number; col: number }> | undefined
  if (sel && sel.length) return sel
  const all: Array<{ row: number; col: number }> = []
  for (let r = 0; r < t.rows; r++) for (let c = 0; c < t.cols; c++) all.push({ row: r, col: c })
  return all
}

function eachTarget(t: Table, fn: (text: any, cell: any) => void): Table {
  for (const { row, col } of targetPositions(t)) fn(t.getCellText(row, col), t.getCell(row, col))
  ;(t as any).triggerLayoutWithAnchor?.()
  getCanvas().requestRenderAll()
  return t
}

function firstText(t: Table): any {
  const p = targetPositions(t)[0]
  return p ? t.getCellText(p.row, p.col) : undefined
}

export function setCellTextAlign(t: Table, align: CellAlign): Table { return eachTarget(t, (tx) => tx?.set('textAlign', align)) }
export function setCellVAlign(t: Table, v: CellVAlign): Table { return eachTarget(t, (tx) => tx?.set('verticalAlign', v)) }
export function setCellFontSize(t: Table, n: number): Table { return eachTarget(t, (tx) => tx?.set('fontSize', Math.max(4, n))) }
export function setCellTextColor(t: Table, color: string): Table { return eachTarget(t, (tx) => tx?.set('fill', color)) }
export function setCellBorderStyle(t: Table, style: BorderStyle): Table { return eachTarget(t, (_tx, cell) => cell?.set('strokeDashArray', DASH[style])) }

export function toggleCellBold(t: Table): Table {
  const on = firstText(t)?.fontWeight === 'bold'
  return eachTarget(t, (tx) => tx?.set('fontWeight', on ? 'normal' : 'bold'))
}
export function toggleCellItalic(t: Table): Table {
  const on = firstText(t)?.fontStyle === 'italic'
  return eachTarget(t, (tx) => tx?.set('fontStyle', on ? 'normal' : 'italic'))
}

export interface TableTextStyle {
  textAlign: CellAlign; verticalAlign: CellVAlign; bold: boolean; italic: boolean
  fontSize: number; textColor: string; borderStyle: BorderStyle; scope: 'selection' | 'all'
}
export function getTableTextStyle(t: Table): TableTextStyle {
  const tx = firstText(t)
  const cell = (() => { const p = targetPositions(t)[0]; return p ? t.getCell(p.row, p.col) : undefined })() as any
  const dash: number[] = cell?.strokeDashArray ?? []
  const borderStyle: BorderStyle = !dash.length ? 'solid' : dash[0] <= 3 ? 'dotted' : 'dashed'
  const sel = (t as any)._selectedCells as unknown[] | undefined
  return {
    textAlign: (tx?.textAlign as CellAlign) ?? 'center',
    verticalAlign: (tx?.verticalAlign as CellVAlign) ?? 'middle',
    bold: tx?.fontWeight === 'bold',
    italic: tx?.fontStyle === 'italic',
    fontSize: tx?.fontSize ?? 14,
    textColor: typeof tx?.fill === 'string' ? tx.fill : '#333333',
    borderStyle,
    scope: sel && sel.length ? 'selection' : 'all',
  }
}

export function getTableModel(t: Table): TableModel {
  const rows = t.rows
  const cols = t.cols
  const data = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => t.getCellText(r, c)?.text ?? ''))
  return { rows, cols, data }
}
